// Global keyboard navigation for forms. Attach via onKeyDownCapture on a container.
// Enter = forward · Backspace on empty field (or on a button) = backward ·
// ArrowRight/Down = forward, ArrowLeft/Up = backward (row-aware in grids) ·
// Enter/ArrowDown opens a dropdown, Enter selection auto-advances ·
// End = jump to [data-end-target] (when enabled).

export function walkFields(container, selector) {
  return [...container.querySelectorAll(selector)].filter(el => el.offsetParent !== null);
}

export function focusField(el, { open = true } = {}) {
  if (!el) return;
  const tid = el.getAttribute("data-testid") || "";
  if (open && (el.hasAttribute("data-auto-open") || tid.startsWith("enter-batch-"))) { el.click(); return; }
  el.focus();
  if (el.select) { try { el.select(); } catch {} }
}

function focusSave(container) {
  container.querySelector('button[data-testid^="save-"]:not([disabled])')?.focus();
}

// When a record was opened from a list row via keyboard, Backspace/Left on the
// record's first field closes the record and returns focus to its list row.
function returnToList(e) {
  const tid = window.__kbReturnTo;
  if (!tid) return;
  window.__kbReturnTo = null;
  e.preventDefault(); e.stopPropagation();
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
  setTimeout(() => {
    const row = document.querySelector(`tr[data-testid="${tid}"]`);
    if (row) { row.tabIndex = -1; row.focus(); }
  }, 220);
}

function focusEndTarget(container) {
  const na = container.querySelector("[data-end-target]") || container.querySelector("[data-net-amount]");
  if (!na) return false;
  na.focus(); if (na.select) { try { na.select(); } catch {} }
  na.scrollIntoView({ block: "nearest" });
  return true;
}

function isEmptyCombobox(t) {
  return t.hasAttribute("data-placeholder") || !!t.querySelector("[data-placeholder]");
}

const isToggleable = (t) => t.type === "checkbox" || ["checkbox", "switch"].includes(t.getAttribute("role"));
const isCombobox = (t) => t.getAttribute("role") === "combobox";

// Arrow/Backspace walks also include plain buttons so focus can move
// field -> button -> field without activating anything (Enter still clicks).
const WITH_BUTTONS = (selector) => `${selector}, button:not([disabled]):not([aria-label="Close"])`;

function openCombobox(t) {
  t.setAttribute("data-kb-openval", t.textContent || "");
  t.click();
}

// Auto-advance: when a keyboard-opened dropdown closes with a changed value,
// focus returns to the trigger and we move to the next field. Esc-cancel leaves
// the value unchanged, so focus stays and the next Enter advances instead.
if (typeof document !== "undefined" && !window.__kbNavDocInit) {
  window.__kbNavDocInit = true;
  window.__kbNavVer = 5;
  document.addEventListener("focusin", (e) => {
    const trg = e.target;
    if (!(trg instanceof HTMLElement)) return;
    // sweep stale open-flags: dropdown closed while focus moved elsewhere
    document.querySelectorAll("[data-kb-openval]").forEach(el => {
      if (el !== trg && el.getAttribute("aria-expanded") !== "true") el.removeAttribute("data-kb-openval");
    });
    const prevVal = trg.getAttribute("data-kb-openval");
    if (prevVal === null) return;
    trg.removeAttribute("data-kb-openval");
    if ((trg.textContent || "") === prevVal) return; // Esc-cancel / unchanged value
    const container = trg.closest("[data-kb-nav]");
    if (!container) return;
    const fields = walkFields(container, container.getAttribute("data-kb-nav"));
    const next = fields[fields.indexOf(trg) + 1];
    if (next) focusField(next); else focusSave(container);
  });
  // Backspace inside an open dropdown list dismisses it via a synthetic outside
  // pointerdown (Radix DismissableLayer) — trigger.click() would just reopen it,
  // and synthetic Escape would trip window-level Esc handlers.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Backspace") return;
    const t = e.target;
    if (!(t instanceof HTMLElement) || !t.closest('[role="listbox"], [role="option"]')) return;
    const trg = document.querySelector('[role="combobox"][aria-expanded="true"]');
    if (!trg) return;
    e.preventDefault(); e.stopPropagation();
    document.body.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true, cancelable: true }));
    document.body.dispatchEvent(new PointerEvent("pointerup", { bubbles: true, cancelable: true }));
    setTimeout(() => trg.focus(), 80);
  }, true);
}

export function backspacePrev(e, container, selector) {
  const t = e.target;
  if (!t || !t.tagName || t.tagName === "TEXTAREA") return;
  if (t.closest("[cmdk-input]")) return;
  if (t.tagName === "BUTTON" || isCombobox(t) || isToggleable(t)) {
    // Backspace on a focused button/checkbox/dropdown -> previous field
  } else {
    if (t.tagName !== "INPUT") return;
    if (["checkbox", "radio", "button", "submit", "file", "hidden"].includes(t.type)) return;
    if (t.readOnly || t.disabled) return;
    if (t.value !== "") return; // native delete while the field has content
  }
  e.preventDefault(); e.stopPropagation();
  const fields = walkFields(container, WITH_BUTTONS(selector));
  const prev = fields[fields.indexOf(t) - 1];
  if (prev) focusField(prev, { open: false });
  else returnToList(e);
}

function rowOf(el) {
  return el.closest("[data-kb-row]") || el.closest("tr");
}

export function arrowNav(e, container, selector) {
  const t = e.target;
  const key = e.key;
  if (!t || !t.tagName || t.tagName === "TEXTAREA") return false;
  if (t.closest("[cmdk-input]") || t.closest("[cmdk-item]")) return false;
  if (t.tagName === "INPUT" && ["date", "datetime-local", "time", "month", "week"].includes(t.type)) return false;

  const fields = walkFields(container, WITH_BUTTONS(selector));
  const idx = fields.indexOf(t);
  if (idx === -1) return false;
  const fwd = key === "ArrowRight" || key === "ArrowDown";

  // text-like inputs: horizontal arrows keep native caret movement unless at a boundary
  if (t.tagName === "INPUT" && ["text", "email", "tel", "url", "search", "password"].includes(t.type)) {
    if (key === "ArrowLeft" && !(t.selectionStart === 0 && t.selectionEnd === 0)) return false;
    if (key === "ArrowRight" && t.selectionStart !== t.value.length) return false;
  }

  // closed dropdown: ArrowDown opens it
  if (isCombobox(t) && key === "ArrowDown") {
    e.preventDefault(); e.stopPropagation();
    openCombobox(t);
    return true;
  }

  // grids: Up/Down jump between rows staying in the same column
  if (key === "ArrowUp" || key === "ArrowDown") {
    const row = rowOf(t);
    if (row) {
      const col = fields.filter(f => rowOf(f) === row).indexOf(t);
      const rows = [...new Set(fields.map(rowOf))];
      const targetRow = rows[rows.indexOf(row) + (key === "ArrowDown" ? 1 : -1)];
      if (targetRow) {
        const tf = fields.filter(f => rowOf(f) === targetRow);
        const target = tf[Math.min(Math.max(col, 0), tf.length - 1)];
        if (target) { e.preventDefault(); e.stopPropagation(); focusField(target, { open: false }); return true; }
      }
    }
  }

  e.preventDefault(); e.stopPropagation();
  const target = fields[idx + (fwd ? 1 : -1)];
  if (target) focusField(target, { open: false });
  else if (!fwd) returnToList(e);
  return true;
}

export function createKeyboardNav({ selector, endTarget = false, skipInside = null, addLineBtn = false, onLastField = null } = {}) {
  return (e) => {
    const t = e.target;
    if (!t || !t.tagName || t.tagName === "TEXTAREA") return;
    if (t.closest("[cmdk-input]") || t.closest("[cmdk-item]")) return;
    if (skipInside && t.closest(skipInside)) return;
    e.currentTarget.setAttribute("data-kb-nav", selector);

    if (e.key === "Backspace") { backspacePrev(e, e.currentTarget, selector); return; }
    if (e.key.startsWith("Arrow")) { arrowNav(e, e.currentTarget, selector); return; }

    if (e.key === "End") {
      if (!endTarget) return;
      if (t.tagName === "INPUT" && t.type === "text" && !t.readOnly) return;
      if (focusEndTarget(e.currentTarget)) { e.preventDefault(); e.stopPropagation(); }
      return;
    }
    if (e.key !== "Enter") return;

    if (addLineBtn && (t.getAttribute("data-testid") || "") === "add-line-btn") {
      e.preventDefault(); e.stopPropagation();
      const container = e.currentTarget;
      // Double-Enter on an empty item row: close item entry -> summary/footer
      const its = [...container.querySelectorAll('[data-testid^="line-item-"]')];
      const last = its[its.length - 1];
      if (last && isEmptyCombobox(last)) { if (!focusEndTarget(container)) focusSave(container); return; }
      if (!t.disabled) {
        t.click();
        setTimeout(() => {
          const its2 = [...container.querySelectorAll('[data-testid^="line-item-"]')];
          const last2 = its2[its2.length - 1];
          if (last2) last2.click();
        }, 80);
      }
      return;
    }

    const role = t.getAttribute("role");
    if (t.tagName === "BUTTON" && role !== "combobox" && !isToggleable(t)) return; // plain buttons: native Enter = click

    const container = e.currentTarget;
    const fields = walkFields(container, selector);
    const idx = fields.indexOf(t);
    if (idx === -1) return;
    e.preventDefault(); e.stopPropagation();

    if (role === "combobox" && isEmptyCombobox(t)) { openCombobox(t); return; } // Enter opens an empty dropdown

    const finish = () => { if (onLastField) onLastField(container); else focusSave(container); };
    const next = fields[idx + 1];
    if (isToggleable(t)) {
      t.click();
      if (next) setTimeout(() => focusField(next), 60); else finish();
      return;
    }
    if (next) focusField(next); else finish();
  };
}

const SIMPLE_SEL = 'input:not([disabled]):not([type="hidden"]), [role="combobox"], button[role="checkbox"], button[role="switch"]';
export const handleSimpleFormNav = createKeyboardNav({ selector: SIMPLE_SEL });

// List pages: every table row becomes keyboard-focusable (tabIndex -1 via a
// MutationObserver, so it covers re-renders). Up/Down walk rows; Enter opens
// the record (its edit action / first link/button / row click) and remembers
// the row so Backspace/Left on the record's first field returns focus here.
function markRows(root) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll("tbody tr").forEach(tr => {
    if (!tr.hasAttribute("data-kb-tr")) { tr.setAttribute("data-kb-tr", "1"); tr.tabIndex = -1; }
  });
}

export function initGlobalKbNav() {
  if (window.__kbListNavInit) return;
  window.__kbListNavInit = true;
  markRows(document);
  new MutationObserver(muts => muts.forEach(m => m.addedNodes.forEach(n => {
    if (n.nodeType !== 1) return;
    if (n.tagName === "TR" && !n.hasAttribute("data-kb-tr")) { n.setAttribute("data-kb-tr", "1"); n.tabIndex = -1; }
    markRows(n);
  }))).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener("keydown", (e) => {
    const tr = e.target;
    if (!(tr instanceof HTMLElement) || tr.tagName !== "TR") return;
    const rows = [...(tr.closest("tbody")?.querySelectorAll("tr") || [])].filter(r => r.offsetParent !== null);
    const i = rows.indexOf(tr);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault(); e.stopPropagation();
      rows[i + (e.key === "ArrowDown" ? 1 : -1)]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault(); e.stopPropagation();
      window.__kbReturnTo = tr.getAttribute("data-testid") || null;
      const act = tr.querySelector('button[data-testid^="edit-"]') || tr.querySelector("a") || tr.querySelector("button:not([disabled])");
      if (act) act.click(); else tr.click();
    }
  }, true);

  // Whenever a record opened from a row is closed with Esc, hand focus back to its row
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !window.__kbReturnTo) return;
    const tid = window.__kbReturnTo;
    window.__kbReturnTo = null;
    setTimeout(() => {
      const row = document.querySelector(`tr[data-testid="${tid}"]`);
      if (row) { row.tabIndex = -1; row.focus(); }
    }, 250);
  }, true);
}


// Global fallback: Enter = next field, Backspace-on-empty = previous field, on ANY
// form/page — including ones without a container-level onKeyDownCapture handler.
// Bubble phase: existing container handlers (capture) run first and stopPropagation
// when they handle a key, so this never double-fires and never overrides the
// specialised transaction-page navigation.
export function initGlobalUppercase() {
  const EXCLUDED_TYPES = new Set(["email", "password", "number", "date", "datetime-local", "month", "time", "url", "tel", "checkbox", "radio", "file", "hidden", "range", "color"]);
  document.addEventListener("input", (e) => {
    if (e.isComposing) return;
    const t = e.target;
    if (!t) return;
    const isTextarea = t.tagName === "TEXTAREA";
    const isInput = t.tagName === "INPUT";
    if (!isTextarea && !isInput) return;
    if (isInput && EXCLUDED_TYPES.has((t.getAttribute("type") || "text").toLowerCase())) return;
    if (t.readOnly || t.disabled || t.dataset.noUppercase !== undefined) return;
    const cur = t.value;
    const up = cur.toUpperCase();
    if (cur === up) return;
    const pos = t.selectionStart;
    const proto = isTextarea ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, "value").set.call(t, up);
    try { t.setSelectionRange(pos, pos); } catch {}
    t.dispatchEvent(new Event("input", { bubbles: true }));
  }, true); // capture phase — uppercase before React's onChange reads the value
}

export function initGlobalFormNav() {
  if (window.__kbFormNavInit) return;
  window.__kbFormNavInit = true;
  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || e.isComposing) return;
    if (e.key !== "Enter" && e.key !== "Backspace") return;
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.tagName === "TR") return; // list-row nav owns TR keys
    if (t.tagName === "TEXTAREA") return; // native newline / deletion in textareas
    if (t.closest("[cmdk-input]") || t.closest("[cmdk-item]")) return;
    if (t.closest('[role="listbox"], [role="option"]')) return; // open dropdown owns the key
    if (document.querySelector('[role="listbox"]')) return;
    const container = t.closest('[role="dialog"]') || t.closest("main") || document.body;
    container.setAttribute("data-kb-nav", SIMPLE_SEL);

    if (e.key === "Backspace") { backspacePrev(e, container, SIMPLE_SEL); return; }

    // Enter
    const role = t.getAttribute("role");
    if (t.tagName === "BUTTON" && role !== "combobox" && !isToggleable(t)) return; // native click
    const fields = walkFields(container, SIMPLE_SEL);
    const idx = fields.indexOf(t);
    if (idx === -1) return;
    if (role === "combobox" && isEmptyCombobox(t)) {
      e.preventDefault(); e.stopPropagation(); openCombobox(t); return; // Enter opens an empty dropdown
    }
    const next = fields[idx + 1];
    if (!next) {
      const save = container.querySelector('button[data-testid^="save-"]:not([disabled])');
      if (!save) return; // no next field: native behaviour (e.g. login form submit) proceeds
      e.preventDefault(); e.stopPropagation(); save.focus(); return;
    }
    e.preventDefault(); e.stopPropagation();
    if (isToggleable(t)) { t.click(); setTimeout(() => focusField(next), 60); return; }
    focusField(next);
  }); // bubble phase — intentionally not capture
}
